import { Field, FieldShell, SelectField } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { Option, Translate } from './customer-types'

export interface CustomerFormValues {
	first_name: string
	last_name_1: string
	last_name_2: string
	type: string
	status: string
	preferred_channel: string
	preferred_locale: string
	tags: string
	org_unit_id: string
	assigned_user_id: string
}

export interface ContactDraft {
	/** Client-side key of the row (never sent). */
	id: string
	type: string
	value: string
	label: string
	is_primary: boolean
}

/**
 * Props for {@link CustomerFormView}. Validation, reachability and the duplicate
 * lookup live in the page hook; this view only renders their results.
 *
 * @property channels - Every preferred channel with whether it is reachable (disabled otherwise).
 * @property duplicate - The lookup result for the last blurred contact, or null.
 * @property typeLocked - True once the customer is converted (lead → customer is one-way).
 */
export interface CustomerFormViewProps {
	t: Translate
	mode: 'create' | 'edit'
	values: CustomerFormValues
	contacts: ContactDraft[]
	consentChannels: string[]
	errors: Record<string, string>
	formError: string | null
	channels: Array<{ id: string; enabled: boolean }>
	orgUnits: Option[]
	users: Option[]
	typeLocked: boolean
	duplicate: { visible: Option[]; hidden: number } | null
	isBusy: boolean
	onChange: (field: keyof CustomerFormValues, value: string) => void
	onContactChange: (index: number, field: keyof ContactDraft, value: string | boolean) => void
	onContactBlur: (index: number) => void
	onAddContact: () => void
	onToggleConsent: (channel: string) => void
	onSubmit: () => void
	onCancel: () => void
}

const LOCALES = ['es-ES', 'gl-ES', 'en-GB', 'pt-PT']

type TextField = 'first_name' | 'last_name_1' | 'last_name_2' | 'tags'

function TextInput({ props, field, label }: { props: CustomerFormViewProps; field: TextField; label: string }) {
	const { t, values, errors, onChange } = props
	return (
		<div>
			<Field label={t(label)} value={values[field]} onChange={(e) => onChange(field, e.target.value)} />
			{errors[field] ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{errors[field]}
				</p>
			) : null}
		</div>
	)
}

function ClassificationFields({ props }: { props: CustomerFormViewProps }) {
	const { t, values, errors, onChange } = props
	return (
		<>
			{props.typeLocked ? (
				<p className="text-ds-sm text-ds-text">{`${t('profile.type')}: ${t(`type.${values.type}`)}`}</p>
			) : (
				<SelectField
					label={t('profile.type')}
					value={values.type}
					onValueChange={(v) => onChange('type', v)}
					options={['lead', 'customer'].map((id) => ({ id, text: t(`type.${id}`) }))}
				/>
			)}
			<SelectField
				label={t('profile.status')}
				value={values.status}
				onValueChange={(v) => onChange('status', v)}
				options={['active', 'inactive'].map((id) => ({ id, text: t(`status.${id}`) }))}
			/>
			<FieldShell label={t('profile.preferredChannel')} error={errors.preferred_channel}>
				<select
					value={values.preferred_channel}
					onChange={(e) => onChange('preferred_channel', e.target.value)}
					className="h-10 rounded-ds-md border border-ds-border px-3"
				>
					<option value="">{t('profile.none')}</option>
					{props.channels.map((c) => (
						<option key={c.id} value={c.id} disabled={!c.enabled}>
							{t(`channel.${c.id}`)}
						</option>
					))}
				</select>
			</FieldShell>
			<SelectField
				label={t('profile.preferredLanguage')}
				value={values.preferred_locale}
				onValueChange={(v) => onChange('preferred_locale', v)}
				options={[{ id: '', text: t('profile.none') }, ...LOCALES.map((id) => ({ id, text: id }))]}
			/>
		</>
	)
}

function AssignmentFields({ props }: { props: CustomerFormViewProps }) {
	const { t, values, errors, onChange } = props
	return (
		<>
			<SelectField
				label={t('profile.orgUnit')}
				value={values.org_unit_id}
				error={errors.org_unit_id}
				onValueChange={(v) => onChange('org_unit_id', v)}
				options={props.orgUnits.map((o) => ({ id: o.id, text: o.label }))}
				placeholder={t('profile.none')}
			/>
			<SelectField
				label={t('profile.assignedUser')}
				value={values.assigned_user_id}
				error={errors.assigned_user_id}
				onValueChange={(v) => onChange('assigned_user_id', v)}
				options={props.users.map((o) => ({ id: o.id, text: o.label }))}
				placeholder={t('profile.none')}
			/>
		</>
	)
}

function ProfileFields({ props }: { props: CustomerFormViewProps }) {
	return (
		<>
			<TextInput props={props} field="first_name" label="profile.firstName" />
			<TextInput props={props} field="last_name_1" label="profile.lastName1" />
			<TextInput props={props} field="last_name_2" label="profile.lastName2" />
			<ClassificationFields props={props} />
			<TextInput props={props} field="tags" label="profile.tags" />
			<AssignmentFields props={props} />
		</>
	)
}

function ContactRow({ props, contact, index }: { props: CustomerFormViewProps; contact: ContactDraft; index: number }) {
	const { t } = props
	return (
		<div className="flex items-end gap-2">
			<SelectField
				label={t('contact.type')}
				value={contact.type}
				onValueChange={(v) => props.onContactChange(index, 'type', v)}
				options={['email', 'mobile', 'landline'].map((id) => ({ id, text: t(`contact.${id}`) }))}
			/>
			<div>
				<Field
					label={t('contact.value')}
					value={contact.value}
					onChange={(e) => props.onContactChange(index, 'value', e.target.value)}
					onBlur={() => props.onContactBlur(index)}
				/>
				{props.errors[`contact_${index}`] ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{props.errors[`contact_${index}`]}
					</p>
				) : null}
			</div>
		</div>
	)
}

function DuplicateNotice({
	t,
	duplicate,
}: {
	t: Translate
	duplicate: NonNullable<CustomerFormViewProps['duplicate']>
}) {
	return (
		<p role="status" className="text-ds-sm text-ds-text-muted">
			{duplicate.visible.length > 0
				? `${t('form.duplicate')}: ${duplicate.visible.map((o) => o.label).join(', ')}. `
				: ''}
			{duplicate.hidden > 0 ? t('form.duplicateHidden', { count: duplicate.hidden }) : ''}
		</p>
	)
}

function ConsentChoices({ props }: { props: CustomerFormViewProps }) {
	const { t } = props
	return (
		<>
			<p className="text-ds-sm text-ds-text">{t('form.initialConsent')}</p>
			<div className="flex gap-3">
				{['email', 'sms', 'whatsapp', 'phone'].map((channel) => (
					<label key={channel} className="flex items-center gap-1 text-ds-sm">
						<input
							type="checkbox"
							checked={props.consentChannels.includes(channel)}
							onChange={() => props.onToggleConsent(channel)}
						/>
						{t(`channel.${channel}`)}
					</label>
				))}
			</div>
		</>
	)
}

function ContactFields({ props }: { props: CustomerFormViewProps }) {
	const { t } = props
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="text-ds-sm font-semibold text-ds-text">{t('form.contactPoints')}</legend>
			{props.contacts.map((contact, index) => (
				<ContactRow key={contact.id} props={props} contact={contact} index={index} />
			))}
			{props.duplicate ? <DuplicateNotice t={t} duplicate={props.duplicate} /> : null}
			<Button size="sm" variant="muted" onPress={props.onAddContact}>
				{t('actions.addContact')}
			</Button>
			<ConsentChoices props={props} />
		</fieldset>
	)
}

/** Create / edit form of a customer (organism). Contacts and initial consents appear on create only. */
export function CustomerFormView(props: CustomerFormViewProps): ReactElement {
	const { t } = props
	return (
		<form
			className="flex max-w-2xl flex-col gap-4"
			onSubmit={(event) => {
				event.preventDefault()
				props.onSubmit()
			}}
		>
			<h1 className="text-ds-lg font-semibold text-ds-text">
				{t(props.mode === 'create' ? 'form.createTitle' : 'form.editTitle')}
			</h1>
			{props.formError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.formError}
				</p>
			) : null}
			<ProfileFields props={props} />
			{props.mode === 'create' ? <ContactFields props={props} /> : null}
			<div className="flex gap-2">
				<Button type="submit" isDisabled={props.isBusy}>
					{t('actions.save')}
				</Button>
				<Button variant="muted" onPress={props.onCancel}>
					{t('actions.cancel')}
				</Button>
			</div>
		</form>
	)
}
